/**
 * 変数システムスキーマ
 *
 * シナリオ内で使用できるカスタム変数の定義と操作方法を提供します。
 * Phase A-h「独自ステータス」機能の基盤となります。
 */
import { z } from 'zod';
// ============================================
// 変数定義スキーマ
// ============================================
/**
 * 変数の型
 */
export const VariableTypeSchema = z.enum(['number', 'string', 'boolean']);
/**
 * 変数定義
 * シナリオ作成時に定義するカスタム変数
 */
export const VariableDefinitionSchema = z.object({
    /** 変数キー（一意） */
    key: z.string().min(1).max(64),
    /** 変数の型 */
    type: VariableTypeSchema,
    /** デフォルト値 */
    defaultValue: z.union([z.string(), z.number(), z.boolean()]),
    /** UI表示用ラベル */
    label: z.string().optional(),
    /** 説明 */
    description: z.string().optional(),
    /** 最小値（number型のみ） */
    min: z.number().optional(),
    /** 最大値（number型のみ） */
    max: z.number().optional(),
    /** デバッグ画面に表示するか */
    showInDebug: z.boolean().default(true),
    /** 表示アイコン（オプション） */
    icon: z.string().optional(),
    /** 表示色（オプション） */
    color: z.string().optional(),
});
/**
 * シナリオレベルの変数設定
 */
export const ScenarioVariablesSchema = z.object({
    /** 変数定義リスト */
    definitions: z.array(VariableDefinitionSchema).default([]),
});
// ============================================
// 変数操作スキーマ（Phase A-h で実装）
// ============================================
/**
 * 変数操作（mutation）
 * ステップ内で変数を変更するための操作
 */
export const VariableMutationSchema = z.discriminatedUnion('op', [
    // 値を設定
    z.object({
        op: z.literal('set'),
        key: z.string(),
        value: z.union([z.string(), z.number(), z.boolean()]),
    }),
    // 数値をインクリメント
    z.object({
        op: z.literal('increment'),
        key: z.string(),
        amount: z.number().default(1),
    }),
    // 数値をデクリメント
    z.object({
        op: z.literal('decrement'),
        key: z.string(),
        amount: z.number().default(1),
    }),
    // ブール値をトグル
    z.object({
        op: z.literal('toggle'),
        key: z.string(),
    }),
    // 最小/最大値でクランプ
    z.object({
        op: z.literal('clamp'),
        key: z.string(),
        min: z.number().optional(),
        max: z.number().optional(),
    }),
]);
// ============================================
// ヘルパー関数
// ============================================
/**
 * 変数定義からデフォルト値のマップを生成
 */
export function createDefaultVariables(definitions) {
    const result = {};
    for (const def of definitions) {
        result[def.key] = def.defaultValue;
    }
    return result;
}
/**
 * 変数の型をチェック
 */
export function validateVariableType(value, expectedType) {
    switch (expectedType) {
        case 'number':
            return typeof value === 'number' && !Number.isNaN(value);
        case 'string':
            return typeof value === 'string';
        case 'boolean':
            return typeof value === 'boolean';
        default:
            return false;
    }
}
/**
 * 変数値を制約内にクランプ
 */
export function clampVariableValue(value, min, max) {
    let result = value;
    if (min !== undefined && result < min) {
        result = min;
    }
    if (max !== undefined && result > max) {
        result = max;
    }
    return result;
}
/**
 * 変数操作を適用
 */
export function applyVariableMutation(variables, mutation, definitions) {
    const result = { ...variables };
    const def = definitions.find((d) => d.key === mutation.key);
    switch (mutation.op) {
        case 'set': {
            result[mutation.key] = mutation.value;
            break;
        }
        case 'increment': {
            const current = Number(result[mutation.key] ?? 0);
            let newValue = current + mutation.amount;
            if (def?.max !== undefined) {
                newValue = Math.min(newValue, def.max);
            }
            result[mutation.key] = newValue;
            break;
        }
        case 'decrement': {
            const current = Number(result[mutation.key] ?? 0);
            let newValue = current - mutation.amount;
            if (def?.min !== undefined) {
                newValue = Math.max(newValue, def.min);
            }
            result[mutation.key] = newValue;
            break;
        }
        case 'toggle': {
            result[mutation.key] = !result[mutation.key];
            break;
        }
        case 'clamp': {
            const current = Number(result[mutation.key] ?? 0);
            result[mutation.key] = clampVariableValue(current, mutation.min, mutation.max);
            break;
        }
    }
    return result;
}
//# sourceMappingURL=variableSchema.js.map